package com.shadowings.gero.presentation.component.quiz

import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.util.SoundPlayer
import compose.icons.FeatherIcons
import compose.icons.feathericons.RefreshCw
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.avanti_excl_btn
import gero.composeapp.generated.resources.basco
import gero.composeapp.generated.resources.custom_glasses_type_ciclope
import gero.composeapp.generated.resources.custom_glasses_type_gold
import gero.composeapp.generated.resources.custom_glasses_type_pixel
import gero.composeapp.generated.resources.custom_hat_type_beret
import gero.composeapp.generated.resources.custom_hat_type_egypt
import gero.composeapp.generated.resources.custom_hat_type_viking
import gero.composeapp.generated.resources.custom_macchie_type_tigrato
import gero.composeapp.generated.resources.custom_macchie_type_tuxedo
import gero.composeapp.generated.resources.custom_macchie_type_tuxedo_tigrato
import gero.composeapp.generated.resources.cyclope
import gero.composeapp.generated.resources.egypt
import gero.composeapp.generated.resources.gold
import gero.composeapp.generated.resources.levelup_accessory_unlocked_title
import gero.composeapp.generated.resources.levelup_feedback
import gero.composeapp.generated.resources.levelup_streak_restoration_desc_many
import gero.composeapp.generated.resources.levelup_streak_restoration_desc_one
import gero.composeapp.generated.resources.levelup_streak_restoration_title
import gero.composeapp.generated.resources.levelup_subtitle
import gero.composeapp.generated.resources.custom_mask_type_einstein
import gero.composeapp.generated.resources.custom_mask_type_pirata
import gero.composeapp.generated.resources.custom_mask_type_riccone
import gero.composeapp.generated.resources.levelup_title
import gero.composeapp.generated.resources.masko_einstein
import gero.composeapp.generated.resources.pirate
import gero.composeapp.generated.resources.pixel
import gero.composeapp.generated.resources.rich
import gero.composeapp.generated.resources.tigrato
import gero.composeapp.generated.resources.tuxedo
import gero.composeapp.generated.resources.tuxedo_tigrato
import gero.composeapp.generated.resources.viking
import io.github.vinceglb.confettikit.compose.ConfettiKit
import io.github.vinceglb.confettikit.core.Party
import io.github.vinceglb.confettikit.core.Position
import io.github.vinceglb.confettikit.core.emitter.Emitter
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlin.time.DurationUnit
import kotlin.time.toDuration

/**
 * Hats unlocked by reaching a level — mirrors AlynCharacterScreen's `hatTypeLevelRequirements`
 * (0, 5, 10, 20; the explorer hat at 0 has no requirement so it's not listed here). Kept as a
 * simple level->hat lookup rather than sharing a data structure with that screen, since this is
 * only used to decide what to show right after a level-up.
 */
private fun hatUnlockedAtLevel(level: Int): Pair<DrawableResource, StringResource>? = when (level) {
    5 -> Res.drawable.basco to Res.string.custom_hat_type_beret
    10 -> Res.drawable.viking to Res.string.custom_hat_type_viking
    20 -> Res.drawable.egypt to Res.string.custom_hat_type_egypt
    else -> null
}

/**
 * Nilo's spot patterns unlocked by reaching a level — mirrors NiloCharacterScreen's
 * `macchieTypeLevelRequirements` (0, 6, 12, 24; the base "macchie" pattern at 0 has no requirement
 * so it's not listed here). Levels were deliberately chosen so they never collide with the other
 * characters' accessory levels (Alyn 5/10/20, Zeta 13/21/25), so a single level never needs to
 * announce more than one accessory.
 */
private fun macchieUnlockedAtLevel(level: Int): Pair<DrawableResource, StringResource>? = when (level) {
    6 -> Res.drawable.tigrato to Res.string.custom_macchie_type_tigrato
    12 -> Res.drawable.tuxedo to Res.string.custom_macchie_type_tuxedo
    24 -> Res.drawable.tuxedo_tigrato to Res.string.custom_macchie_type_tuxedo_tigrato
    else -> null
}

/**
 * Zeta's glasses styles unlocked by reaching a level — mirrors ZetaCharacterScreen's
 * `glassesTypeLevelRequirements` (0, 13, 21, 25; the base glasses at 0 have no requirement so
 * they're not listed here). Never collides with Alyn's 5/10/20 or Nilo's 6/12/24.
 */
private fun glassesUnlockedAtLevel(level: Int): Pair<DrawableResource, StringResource>? = when (level) {
    13 -> Res.drawable.gold to Res.string.custom_glasses_type_gold
    21 -> Res.drawable.pixel to Res.string.custom_glasses_type_pixel
    25 -> Res.drawable.cyclope to Res.string.custom_glasses_type_ciclope
    else -> null
}

/**
 * Masko's masks unlocked by reaching a level — mirrors MaskoCharacterScreen's
 * `maskTypeLevelRequirements` (0, 22, 30, 40; the base mask at 0 has no requirement so it's not
 * listed here). Deliberately high since Masko unlocks near the end of the curriculum. Never
 * collides with Alyn's 5/10/20, Nilo's 6/12/24, or Zeta's 13/21/25.
 */
private fun maskUnlockedAtLevel(level: Int): Pair<DrawableResource, StringResource>? = when (level) {
    22 -> Res.drawable.pirate to Res.string.custom_mask_type_pirata
    30 -> Res.drawable.rich to Res.string.custom_mask_type_riccone
    40 -> Res.drawable.masko_einstein to Res.string.custom_mask_type_einstein
    else -> null
}

/**
 * Mirrors the "Personaggi" tab's own gate (`BottomBar.isSectionLocked`/`ChaptersScreen.navigateToTab`,
 * both hardcode 10) — a player can hit a hat-unlocking level well before reaching this many total
 * stars, since XP accrues from combo/daily bonuses independently of star progression. Announcing a
 * hat unlock here before the player can even open the character screen would point them at a
 * screen they can't reach yet, so the callout waits until this gate is actually cleared.
 */
private const val CHARACTERS_SCREEN_REQUIRED_STARS = 10

@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun QuizLevelUpComposablePreview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 7,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData()
            )
        }
    }
}

/** level=5, non-pro user: the hat-unlock callout (Beret) should show under the title. */
@Preview(name = "Level 5 - hat unlocked")
@Preview(name = "Level 5 - hat unlocked - Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun QuizLevelUpComposableHatUnlockedPreview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 5,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 10))
            )
        }
    }
}

/** level=10, non-pro user: the hat-unlock callout (Viking) should show under the title. */
@Preview(name = "Level 10 - hat unlocked")
@Composable
private fun QuizLevelUpComposableHatUnlockedLevel10Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 10,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 10))
            )
        }
    }
}

/** level=20, non-pro user: the hat-unlock callout (Pharaoh) should show under the title. */
@Preview(name = "Level 20 - hat unlocked")
@Composable
private fun QuizLevelUpComposableHatUnlockedLevel20Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 20,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 10))
            )
        }
    }
}

/** level=6, non-pro user: the macchie-unlock callout (Tigrato) should show under the title. */
@Preview(name = "Level 6 - macchie unlocked")
@Composable
private fun QuizLevelUpComposableMacchieUnlockedLevel6Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 6,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 10))
            )
        }
    }
}

/** level=12, non-pro user: the macchie-unlock callout (Tuxedo) should show under the title. */
@Preview(name = "Level 12 - macchie unlocked")
@Composable
private fun QuizLevelUpComposableMacchieUnlockedLevel12Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 12,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 10))
            )
        }
    }
}

/** level=24, non-pro user: the macchie-unlock callout (Tuxedo Tigrato) should show under the title. */
@Preview(name = "Level 24 - macchie unlocked")
@Composable
private fun QuizLevelUpComposableMacchieUnlockedLevel24Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 24,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 10))
            )
        }
    }
}

/** level=13, non-pro user past end_verb_1: the glasses-unlock callout (Gold) should show. */
@Preview(name = "Level 13 - glasses unlocked")
@Composable
private fun QuizLevelUpComposableGlassesUnlockedLevel13Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 13,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(
                    isFullVersionUnlocked = false,
                    stars = mapOf("preview" to 10),
                    explanations = setOf(LessonIds.END_VERB_1)
                )
            )
        }
    }
}

/** level=21, non-pro user past end_verb_1: the glasses-unlock callout (Pixel) should show. */
@Preview(name = "Level 21 - glasses unlocked")
@Composable
private fun QuizLevelUpComposableGlassesUnlockedLevel21Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 21,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(
                    isFullVersionUnlocked = false,
                    stars = mapOf("preview" to 10),
                    explanations = setOf(LessonIds.END_VERB_1)
                )
            )
        }
    }
}

/** level=25, non-pro user past end_verb_1: the glasses-unlock callout (Cyclops) should show. */
@Preview(name = "Level 25 - glasses unlocked")
@Composable
private fun QuizLevelUpComposableGlassesUnlockedLevel25Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 25,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(
                    isFullVersionUnlocked = false,
                    stars = mapOf("preview" to 10),
                    explanations = setOf(LessonIds.END_VERB_1)
                )
            )
        }
    }
}

/**
 * level=13 but end_verb_1 not reached yet: NO glasses callout should show — the player hasn't
 * unlocked Zeta at all, so announcing Zeta's glasses would be confusing.
 */
@Preview(name = "Level 13 - Zeta not reached (no callout)")
@Composable
private fun QuizLevelUpComposableGlassesZetaLockedPreview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 13,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 10))
            )
        }
    }
}

/** level=22, non-pro user past ending_vso: the mask-unlock callout (Pirate) should show. */
@Preview(name = "Level 22 - mask unlocked")
@Composable
private fun QuizLevelUpComposableMaskUnlockedLevel22Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 22,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(
                    isFullVersionUnlocked = false,
                    stars = mapOf("preview" to 10),
                    explanations = setOf(LessonIds.ENDING_VSO)
                )
            )
        }
    }
}

/** level=30, non-pro user past ending_vso: the mask-unlock callout (Rich) should show. */
@Preview(name = "Level 30 - mask unlocked")
@Composable
private fun QuizLevelUpComposableMaskUnlockedLevel30Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 30,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(
                    isFullVersionUnlocked = false,
                    stars = mapOf("preview" to 10),
                    explanations = setOf(LessonIds.ENDING_VSO)
                )
            )
        }
    }
}

/** level=40, non-pro user past ending_vso: the mask-unlock callout (Einstein) should show. */
@Preview(name = "Level 40 - mask unlocked")
@Composable
private fun QuizLevelUpComposableMaskUnlockedLevel40Preview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 40,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(
                    isFullVersionUnlocked = false,
                    stars = mapOf("preview" to 10),
                    explanations = setOf(LessonIds.ENDING_VSO)
                )
            )
        }
    }
}

/**
 * level=22 but ending_vso not reached yet: NO mask callout should show — the player hasn't
 * unlocked Masko at all, so announcing Masko's masks would be confusing.
 */
@Preview(name = "Level 22 - Masko not reached (no callout)")
@Composable
private fun QuizLevelUpComposableMaskMaskoLockedPreview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 22,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 10))
            )
        }
    }
}

/** level=5 but PRO user: NO hat callout should show — pro already has every hat. */
@Preview(name = "Level 5 - PRO (no callout)")
@Composable
private fun QuizLevelUpComposableHatAlreadyOwnedPreview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 5,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = true)
            )
        }
    }
}

/**
 * level=5 but under the Personaggi tab's 10-star gate: NO hat callout should show — the player
 * can't open the character screen yet, so announcing an unlock there would be confusing.
 */
@Preview(name = "Level 5 - not enough stars (no callout)")
@Composable
private fun QuizLevelUpComposableCharacterScreenLockedPreview() {
    AppTheme {
        Surface {
            QuizLevelUpComposable(
                level = 5,
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData(isFullVersionUnlocked = false, stars = mapOf("preview" to 2))
            )
        }
    }
}

/**
 * Mirrors [QuizComboFiveComposable] deliberately: same mid-quiz interstitial-page pattern
 * (spliced into the quiz page list right after the answer that triggered it — see
 * `QuizScreen.addExperienceAndCheckLevelUp`), same confetti/feedback-box/"Avanti" structure, so
 * leveling up reads to the player as the same kind of moment as a combo, not an unrelated one-off.
 * Reuses [SoundPlayer.playComboFive] rather than adding a new sound asset across every platform's
 * `SoundPlayer` actual — revisit if a dedicated level-up sound is worth that cost later.
 */
@Composable
fun QuizLevelUpComposable(
    level: Int,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizList: MutableState<List<IQuizContent>>,
    userData: UserData,
    tokensGranted: Int = 1,
) {
    val scope = rememberCoroutineScope()
    val deviceInfo = rememberDeviceInfo()
    val isVisible = pagerState.currentPage == page
    val showConfetti = remember { mutableStateOf(false) }
    LaunchedEffect(isVisible) {
        if (isVisible) {
            showConfetti.value = true
            SoundPlayer.playComboFive()
        }
    }

    // Pro users already have every hat/pattern/glasses unconditionally (see AlynCharacterScreen's,
    // NiloCharacterScreen's and ZetaCharacterScreen's `!userData.isFullVersionUnlocked` checks), so
    // announcing one as "just unlocked" here would be misleading for them. Same for a player who
    // hasn't reached the Personaggi tab's own star gate yet — see CHARACTERS_SCREEN_REQUIRED_STARS
    // above — or, for Zeta/Masko specifically, hasn't reached the lesson that unlocks the
    // character at all (CharacterScreen.kt's `isZetaUnlocked`/`isMaskoUnlocked` checks) — those
    // characters' accessory levels are close enough to when a fast player could reach that lesson
    // that this can't be assumed. Hat/macchie/glasses/mask levels never collide (5/10/20, 6/12/24,
    // 13/21/25, 22/30/40), so at most one of these is ever non-null.
    val unlockedAccessory = if (userData.isFullVersionUnlocked ||
        userData.stars.values.sum() < CHARACTERS_SCREEN_REQUIRED_STARS
    ) {
        null
    } else {
        hatUnlockedAtLevel(level) ?: macchieUnlockedAtLevel(level) ?: run {
            if (userData.explanations.contains(LessonIds.END_VERB_1)) {
                glassesUnlockedAtLevel(level)
            } else {
                null
            }
        } ?: run {
            if (userData.explanations.contains(LessonIds.ENDING_VSO)) {
                maskUnlockedAtLevel(level)
            } else {
                null
            }
        }
    }

    val onNext: () -> Unit = {
        if (page < quizList.value.lastIndex) {
            scope.launch {
                pagerState.animateScrollToPage(page + 1, animationSpec = tween(500))
            }
        } else {
            navController.popBackStack()
        }
    }

    BoxWithConstraints(Modifier.fillMaxSize().semantics { testTag = "quiz_level_up" }) {
        val isLandscape = maxWidth > maxHeight

        if (isLandscape) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(24.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = stringResource(Res.string.levelup_title),
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                            .semantics { testTag = "level_up_title" }
                    )
                    Text(
                        text = stringResource(Res.string.levelup_subtitle, level),
                        style = MaterialTheme.typography.titleMedium,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                            .semantics { testTag = "level_up_subtitle" }
                    )
                }

                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    AlynFeedbackBox(
                        face = Faces.Happy,
                        text = stringResource(Res.string.levelup_feedback),
                        userData = userData,
                        isLandscape = true
                    )

                    if (unlockedAccessory != null) {
                        UnlockedAccessoryCallout(
                            drawable = unlockedAccessory.first,
                            nameRes = unlockedAccessory.second,
                            deviceInfo = deviceInfo
                        )
                    }
                    StreakRestorationUnlockedCallout(tokensGranted = tokensGranted, deviceInfo = deviceInfo)

                    GeroButton(
                        title = stringResource(Res.string.avanti_excl_btn),
                        testTagId = "level_up_btn_next",
                        modifier = Modifier.fillMaxWidth().height(64.dp).padding(horizontal = 8.dp)
                    ) {
                        onNext()
                    }
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .align(Alignment.Center)
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = stringResource(Res.string.levelup_title),
                    style = MaterialTheme.typography.displayLarge,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth().semantics { testTag = "level_up_title" }
                )
                Text(
                    text = stringResource(Res.string.levelup_subtitle, level),
                    style = MaterialTheme.typography.headlineMedium,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth().semantics { testTag = "level_up_subtitle" }
                )
                Spacer(Modifier.size(24.dp))

                AlynFeedbackBox(
                    face = Faces.Happy,
                    text = stringResource(Res.string.levelup_feedback),
                    userData = userData
                )
                if (unlockedAccessory != null) {
                    UnlockedAccessoryCallout(
                        drawable = unlockedAccessory.first,
                        nameRes = unlockedAccessory.second,
                        deviceInfo = deviceInfo
                    )
                    Spacer(Modifier.size(16.dp))
                }
                StreakRestorationUnlockedCallout(tokensGranted = tokensGranted, deviceInfo = deviceInfo)
            }

            Column(
                modifier = Modifier.fillMaxWidth().align(Alignment.BottomCenter)
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                GeroButton(
                    title = stringResource(Res.string.avanti_excl_btn),
                    testTagId = "level_up_btn_next",
                    modifier = Modifier.fillMaxWidth().height(64.dp)
                ) {
                    onNext()
                }
            }
        }

        if (showConfetti.value) {
            ConfettiKit(
                modifier = Modifier.fillMaxSize(),
                parties = listOf(
                    Party(
                        speed = 0f, maxSpeed = 30f, damping = 0.9f, spread = 360,
                        position = Position.Relative(0.0, 0.3),
                        emitter = Emitter(duration = 2L.toDuration(DurationUnit.SECONDS)).max(100)
                    )
                )
            )
            ConfettiKit(
                modifier = Modifier.fillMaxSize(),
                parties = listOf(
                    Party(
                        angle = 180,
                        speed = 0f, maxSpeed = 30f, damping = 0.9f, spread = 360,
                        position = Position.Relative(1.0, 0.3),
                        emitter = Emitter(duration = 5L.toDuration(DurationUnit.SECONDS)).max(100)
                    )
                )
            )
        }
    }
}

/**
 * Shown on every level-up (not level-gated, unlike the hat/macchie/glasses/mask callouts above) to
 * announce the Streak Restoration token(s) just granted — see QuizScreen.addExperienceAndCheckLevelUp
 * and UserDataRepository.ensureStreakRestorationTokensUpTo.
 */
@Composable
private fun StreakRestorationUnlockedCallout(
    tokensGranted: Int,
    deviceInfo: com.shadowings.gero.presentation.util.DeviceInfo,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(MaterialTheme.colorScheme.tertiaryContainer.copy(alpha = 0.35f))
            .padding(12.dp)
            .semantics { testTag = "level_up_streak_restoration_unlocked" },
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = FeatherIcons.RefreshCw,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.tertiary,
            modifier = Modifier.size(36.dp)
        )
        Column {
            Text(
                text = stringResource(Res.string.levelup_streak_restoration_title),
                style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.Bold),
                color = MaterialTheme.colorScheme.onTertiaryContainer
            )
            Text(
                text = if (tokensGranted <= 1) {
                    stringResource(Res.string.levelup_streak_restoration_desc_one)
                } else {
                    stringResource(Res.string.levelup_streak_restoration_desc_many, tokensGranted)
                },
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onTertiaryContainer
            )
        }
    }
}

@Composable
private fun UnlockedAccessoryCallout(
    drawable: DrawableResource,
    nameRes: StringResource,
    deviceInfo: com.shadowings.gero.presentation.util.DeviceInfo,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.25f))
            .padding(12.dp)
            .semantics { testTag = "level_up_accessory_unlocked" },
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            painter = painterResource(drawable),
            contentDescription = stringResource(nameRes),
            modifier = Modifier.size(48.dp)
        )
        Column {
            Text(
                text = stringResource(Res.string.levelup_accessory_unlocked_title),
                style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.Bold),
                color = MaterialTheme.colorScheme.onPrimaryContainer
            )
            Text(
                text = stringResource(nameRes),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onPrimaryContainer
            )
        }
    }
}
